/**
 * Drives the Keeper Duel screen. The duel (input + keeper AI + outcomes) lives inside a WebView game
 * runtime; this hook owns the economy bridge: it boots the game on `ready`, reports the finished run
 * to the server, and syncs coins from the SERVER response only (never a client-computed total). The
 * server decides every award + the daily cap, so a spoofed run can't mint coins.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApiClient, extractErrorMessage } from '../../../helpers/apiClient.helper';
import { useEconomyStore } from '../../shared/stores/economyStore';
import { useAnalytics } from '../../analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../analytics/events';
import { startKeeperDuelRun, finishKeeperDuelRun } from '../helpers/keeperDuelApi';
import { buildKeeperDuelGameHtml } from '../helpers/keeperDuelGameHtml';
import {
  KEEPER_DUEL_GAME_KEY,
  KEEPER_DUEL_DAILY_COIN_CAP,
  keeperDuelTierFor,
  type KeeperDuelBridgeMessage,
} from '../constants/keeperDuel.constants';

type Status = 'loading' | 'ready' | 'error';
const TOAST_MS = 2200;

/** Day config pushed into the game runtime once the economy state is known. */
export interface KeeperDuelStartConfig {
  bestStreak: number;
}

/**
 * Intent-based host surface (not raw JS injection) so the same hook drives both the native WebView and
 * the web iframe, and stays unit-testable without either. The platform host components implement this.
 */
export interface KeeperDuelWebViewRef {
  applyStart: (config: KeeperDuelStartConfig) => void;
  reload: () => void;
}

export function useKeeperDuelGame() {
  const apiClient = useApiClient();
  const syncCoins = useEconomyStore((s) => s.syncCoins);
  const { track } = useAnalytics();

  const html = useMemo(() => buildKeeperDuelGameHtml(), []);
  const webViewRef = useRef<KeeperDuelWebViewRef | null>(null);
  const startedTrackedRef = useRef(false);
  const lastStreakRef = useRef(0);

  const [status, setStatus] = useState<Status>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [coinsLeftToday, setCoinsLeftToday] = useState(KEEPER_DUEL_DAILY_COIN_CAP);
  const [capReached, setCapReached] = useState(false);
  const [bestStreak, setBestStreak] = useState(0);
  const [newBest, setNewBest] = useState(false);
  const [runOver, setRunOver] = useState(false);
  const [finalStreak, setFinalStreak] = useState(0);
  const [coinsAwarded, setCoinsAwarded] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);
  const flashToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  const bindWebView = useCallback((ref: KeeperDuelWebViewRef | null) => { webViewRef.current = ref; }, []);

  /** Fetch today's economy state and boot the game runtime with it. Called once the WebView is `ready`. */
  const start = useCallback(async () => {
    setStatus('loading');
    setErrorMessage(null);
    try {
      const cfg = await startKeeperDuelRun(apiClient);
      setCoinsLeftToday(cfg.coinsLeftToday);
      setCapReached(cfg.capReached);
      setBestStreak(cfg.bestStreak);
      setStatus('ready');
      webViewRef.current?.applyStart({ bestStreak: cfg.bestStreak });
    } catch {
      // Offline fallback — boot with defaults so the game is always playable (coins won't bank)
      setStatus('ready');
      webViewRef.current?.applyStart({ bestStreak: 0 });
    }
  }, [apiClient]);

  const handleDuelOver = useCallback(async (msg: Extract<KeeperDuelBridgeMessage, { type: 'duel-over' }>) => {
    setFinalStreak(msg.streak);
    setRunOver(true);
    let awarded = 0; // stays 0 if the server call fails (offline / staging without routes)
    try {
      const res = await finishKeeperDuelRun(apiClient, { runId: msg.runId, streak: msg.streak });
      awarded = res.coinsAwarded;
      // Coins come from the server only — anonymous users get no `coins`, so nothing is synced.
      if (typeof res.coins === 'number') syncCoins(res.coins);
      setCoinsAwarded(res.coinsAwarded);
      setCoinsLeftToday(res.coinsLeftToday);
      setCapReached(res.capReached);
      setBestStreak(res.bestStreak);
      setNewBest(res.newBest);
    } catch (err) {
      // The summary still shows — coins just didn't bank this time.
      setCoinsAwarded(0);
      setNewBest(msg.streak > bestStreak);
      flashToast(extractErrorMessage(err, 'Coins will catch up'));
    } finally {
      track(ANALYTICS_EVENTS.GAME_COMPLETED, {
        game_key: KEEPER_DUEL_GAME_KEY,
        streak: msg.streak,
        near_miss: msg.nearMiss,
        coins_awarded: awarded,
        reason: 'run-end',
      });
    }
  }, [apiClient, syncCoins, flashToast, track, bestStreak]);

  const onMessage = useCallback((event: { nativeEvent: { data: string } }) => {
    let msg: KeeperDuelBridgeMessage | { type: 'load-error' };
    try { msg = JSON.parse(event.nativeEvent.data); } catch { return; }
    switch (msg.type) {
      case 'ready':
        if (!startedTrackedRef.current) {
          startedTrackedRef.current = true;
          track(ANALYTICS_EVENTS.GAME_STARTED, { game_key: KEEPER_DUEL_GAME_KEY });
        }
        void start();
        break;
      case 'goal':
        lastStreakRef.current = msg.streak;
        break;
      case 'duel-over':
        void handleDuelOver(msg);
        break;
      case 'load-error':
        setErrorMessage('Could not load the game');
        setStatus('error');
        break;
      default:
        break;
    }
  }, [start, handleDuelOver, track]);

  const retry = useCallback(() => {
    setStatus('loading');
    setErrorMessage(null);
    webViewRef.current?.reload(); // WebView re-posts `ready` → start() runs again
  }, []);

  const onPlayAgain = useCallback(() => {
    setRunOver(false);
    setFinalStreak(0);
    setCoinsAwarded(0);
    setNewBest(false);
    webViewRef.current?.reload();
  }, []);

  /** Back pressed mid-run — close the session funnel; the view navigates away. */
  const confirmExit = useCallback(() => {
    if (runOver) return; // summary already reported the run
    track(ANALYTICS_EVENTS.GAME_COMPLETED, {
      game_key: KEEPER_DUEL_GAME_KEY,
      streak: lastStreakRef.current,
      coins_awarded: 0,
      reason: 'exit',
    });
  }, [track, runOver]);

  const tier = keeperDuelTierFor(finalStreak);

  return {
    status,
    isLoading: status === 'loading',
    isError: status === 'error',
    errorMessage,
    html,
    coinsLeftToday,
    capReached,
    bestStreak,
    newBest,
    runOver,
    finalStreak,
    coinsAwarded,
    tier,
    toast,
    bindWebView,
    onMessage,
    retry,
    onPlayAgain,
    confirmExit,
  };
}

export type UseKeeperDuelGame = ReturnType<typeof useKeeperDuelGame>;
